"use client";

import type { NextPage } from "next";
import React from "react";

import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import {
  ArrowForward,
  Groups,
  Handshake,
  Lightbulb,
  RocketLaunch,
  TrackChanges,
  VerifiedUser,
  WorkspacePremium,
} from "@mui/icons-material";

// --- DATA & TYPE DEFINITIONS (Pemisahan data dari tampilan) ---

interface MisiItem {
  icon: React.ElementType;
  text: string;
}

interface NilaiItem {
  icon: React.ElementType;
  title: string;
  description: string;
}

const misiItems: MisiItem[] = [
  {
    icon: Lightbulb,
    text: "Menyediakan platform e-commerce yang intuitif, cerdas, dan mudah diakses oleh semua.",
  },
  {
    icon: RocketLaunch,
    text: "Menghubungkan produk UMKM berkualitas ke jutaan calon pelanggan di seluruh Indonesia.",
  },
  {
    icon: Groups,
    text: "Memberikan edukasi, pendampingan, dan membangun komunitas kolaboratif untuk pertumbuhan bersama.",
  },
];

const nilaiItems: NilaiItem[] = [
  {
    icon: WorkspacePremium,
    title: "Pemberdayaan",
    description:
      "Keberhasilan kami diukur dari keberhasilan mitra kami. Kami fokus pada solusi yang memberikan kekuatan dan kemandirian.",
  },
  {
    icon: Lightbulb,
    title: "Inovasi",
    description:
      "Kami tidak pernah berhenti belajar untuk menyederhanakan hal yang kompleks, menjadikan teknologi semakin pintar dan mudah.",
  },
  {
    icon: VerifiedUser,
    title: "Integritas",
    description:
      "Kepercayaan adalah fondasi kami. Kami berkomitmen untuk menjalankan bisnis secara transparan, amanah, dan bertanggung jawab.",
  },
  {
    icon: Handshake,
    title: "Kolaborasi",
    description:
      "Kami percaya pada kekuatan kerja sama. Kami tumbuh bersama mitra, pemerintah, dan sesama penggerak ekonomi digital.",
  },
];

const HeroSection: React.FC = () => (
  <Box
    className="relative flex items-center justify-center text-white text-center p-4"
    sx={{
      height: "70vh",
      minHeight: "400px",
      width: "100%",
      backgroundImage: "url(/assets/images/hero-about-us.webp)",
      backgroundSize: "cover",
      backgroundPosition: "center",
      "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 1,
      },
    }}
  >
    <Box className="relative z-10">
      <Typography
        variant="h2"
        component="h1"
        className="font-extrabold mb-12 leading-tight drop-shadow-lg text-3xl md:text-4xl lg:text-6xl"
        sx={{ fontWeight: 800 }}
      >
        Memberdayakan Jantung Ekonomi Indonesia
      </Typography>
      <Typography
        variant="h6"
        component="p"
        className="max-w-3xl mx-auto drop-shadow-md text-md md:text-xl lg:text-2xl"
      >
        Dibangun dari semangat untuk melihat UMKM naik kelas melalui
        transformasi digital yang cerdas dan mudah.
      </Typography>
    </Box>
  </Box>
);

const IntroSection: React.FC = () => (
  <Box component="section" className="py-16 md:py-24 bg-slate-50">
    <Container maxWidth="md">
      <Typography
        variant="h5"
        component="p"
        align="center"
        className="text-gray-700 leading-relaxed text-lg md:text-xl lg:text-2xl"
      >
        UMKM adalah tulang punggung perekonomian bangsa. Di setiap produk, ada
        semangat, kerja keras, dan mimpi yang luar biasa. <br />
        <strong>PT UMKM Pintar Indonesia</strong> lahir dari keyakinan bahwa
        teknologi seharusnya menjadi jembatan, bukan tembok penghalang. <br />
        Platform <strong>UMKMs</strong> kami ciptakan sebagai partner yang siap
        mengangkat potensi setiap pelaku usaha di nusantara.
      </Typography>
    </Container>
  </Box>
);

const VisiMisiSection: React.FC = () => (
  <Box component="section" className="py-16 md:py-24">
    <Container maxWidth="md">
      <Grid container spacing={6} alignItems="center">
        {/* Visi */}
        <Grid spacing={{ xs: 12, md: 5 }}>
          <Box className="text-center md:text-left">
            <Typography
              variant="h4"
              component="h2"
              gutterBottom
              className="font-bold inline-flex gap-2 items-center"
            >
              <span>
                <TrackChanges
                  sx={{ fontSize: 60, color: "primary.main" }}
                  className="mx-auto md:mx-0"
                />
              </span>
              Visi Kami
            </Typography>
            <Typography variant="body1" className="text-gray-600 text-lg">
              Menjadi ekosistem digital terdepan yang mendorong setiap UMKM di
              Indonesia untuk naik kelas, berdaya saing global, dan menjadi tuan
              rumah di negeri sendiri.
            </Typography>
          </Box>
        </Grid>
        {/* Misi */}
        <Grid spacing={{ xs: 12, md: 7 }}>
          <Paper elevation={3} className="p-6 rounded-lg bg-indigo-50">
            <Typography
              variant="h5"
              component="h3"
              gutterBottom
              sx={{ fontWeight: 700 }}
              className="mb-4"
            >
              Misi Kami
            </Typography>
            <List>
              {misiItems.map((item, index) => (
                <ListItem key={index} disablePadding className="gap-4">
                  <ListItemIcon className="min-w-min">
                    <item.icon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{ className: "text-gray-800" }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

const NilaiSection: React.FC = () => (
  <Box component="section" className="py-16 md:py-24 bg-slate-50">
    <Container maxWidth="lg">
      <Box className="text-center mb-12">
        <Typography variant="h4" component="h2" sx={{ fontWeight: 700 }}>
          Nilai-Nilai Kami
        </Typography>
        <Typography
          variant="body1"
          className="text-gray-600 mt-2 max-w-2xl mx-auto"
        >
          Fondasi yang memandu setiap langkah dan keputusan kami.
        </Typography>
      </Box>
      <Grid container spacing={4}>
        {nilaiItems.map((nilai, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
            <Card
              className="h-full text-center hover:-translate-y-2 transition-transform duration-300 rounded-3xl cursor-pointer"
              sx={{ boxShadow: "0 8px 32px 0 rgba(0,0,0,0.1)" }}
            >
              <CardContent className="p-8">
                <Avatar
                  sx={{
                    bgcolor: "primary.main",
                    width: 64,
                    height: 64,
                    margin: "auto",
                    mb: 3,
                  }}
                >
                  <nilai.icon sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{
                    width: "100%",
                    wordBreak: "break-word",
                    fontWeight: 600,
                  }}
                  gutterBottom
                >
                  {nilai.title}
                </Typography>
                <Typography
                  variant="body2"
                  className="text-gray-600"
                  sx={{ width: "100%", wordBreak: "break-word" }}
                >
                  {nilai.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  </Box>
);

const CtaSection: React.FC = () => (
  <Box component="section" className="py-16 md:py-24 bg-white">
    <Container maxWidth="md" className="text-center">
      <Typography
        variant="h4"
        component="h2"
        sx={{ fontWeight: 700 }}
        gutterBottom
      >
        Mari Tumbuh Bersama
      </Typography>
      <Typography
        variant="body1"
        className="text-gray-600 mt-2 max-w-2xl mx-auto mb-8"
      >
        Jadilah bagian dari gerakan digitalisasi UMKM Indonesia. Lihat bagaimana
        platform UMKMs dapat mengubah bisnis Anda.
      </Typography>
      <Box className="flex justify-center gap-4 flex-wrap">
        <Button
          variant="contained"
          color="primary"
          size="large"
          sx={{ py: 1.5, px: 4, fontWeight: 600 }}
          endIcon={<ArrowForward sx={{ width: 24 }} />}
          href="https://remit.umkms.com/signup"
          target="_blank"
          rel="noopener noreferrer"
        >
          Mulai Sekarang
        </Button>
        <Button
          variant="outlined"
          color="primary"
          size="large"
          href="/kontak"
          sx={{ py: 1.5, px: 4, fontWeight: 600 }}
        >
          Hubungi Tim Kami
        </Button>
      </Box>
    </Container>
  </Box>
);

export const AboutUsSection: NextPage = () => {
  return (
    <Box className="bg-white">
      <HeroSection />
      <IntroSection />
      <VisiMisiSection />
      <NilaiSection />
      <CtaSection />
    </Box>
  );
};
